"use client";

import React from "react";
import { Clock, MapPin, User, DollarSign, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function BarrasDeAccess() {
  const {
    ref: leftColRef,
    isVisible: isLeftVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: rightColRef,
    isVisible: isRightVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const benefits = ["Rapid deployment of technical field teams across the region", "Scalable solutions tailored for GCs and MSPs", "24/7 critical support and emergency response", "Secure data handling and military-grade compliance"];
  const specs = [{
    icon: Clock,
    label: "Response Time",
    value: "Under 4 hours"
  }, {
    icon: MapPin,
    label: "Coverage",
    value: "Kansas City & Regional"
  }, {
    icon: User,
    label: "Support Level",
    value: "Tier 2/3 Certified Techs"
  }, {
    icon: DollarSign,
    label: "Pricing",
    value: "Project-based or Retainer"
  }];
  return <section data-section-id="242322" id="business-services" className="py-16 md:py-24 bg-background text-foreground overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          
          {}
          <div ref={leftColRef} className={`relative rounded-lg overflow-hidden aspect-[4/3] lg:aspect-[4/5] w-full transition-all duration-1000 ease-out ${isLeftVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1645714/img/business_services_execution.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1645714/img/business_services_execution.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1645714/img/business_services_execution.png-wvc-sizes" alt="Professional field service execution" className="object-cover w-full h-full hover:scale-105 transition-transform duration-700 grayscale hover:grayscale-0" loading="lazy" />
            
            {}
            <div className="absolute top-0 right-0 bg-primary text-primary-foreground px-6 py-4 rounded-lg flex flex-col items-center justify-center shadow-2xl border-b border-l border-primary/20">
              <span className="text-2xl md:text-3xl font-bold leading-none font-default">24/7</span>
              <span className="text-[10px] uppercase tracking-widest mt-1 font-mono opacity-90">Availability</span>
            </div>

            {}
            <div className="absolute bottom-6 left-6 border border-white/20 bg-white/5 backdrop-blur-xl text-white px-4 py-2 text-xs tracking-widest uppercase rounded-lg font-mono">
              Service Status: Active
            </div>
          </div>

          {}
          <div ref={rightColRef} className={`flex flex-col gap-8 transition-all duration-1000 delay-200 ease-out ${isRightVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <div className="space-y-4">
              <span className="text-primary text-xs font-bold tracking-widest uppercase font-mono">
                High-Performance Execution
              </span>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-default text-foreground leading-tight tracking-tighter">
                Business <br />
                <span className="text-primary italic">Services</span>
              </h2>
              <p className="text-muted-foreground italic text-lg md:text-xl font-default">
                "Reliable field execution and technical support for modern infrastructure."
              </p>
            </div>

            <div className="text-foreground/80 space-y-4 text-base md:text-lg leading-relaxed font-default">
              <p>
                Our business services focus on high-quality field delivery, ensuring your technical infrastructure is deployed and maintained with absolute precision. We specialize in supporting GCs and MSPs with rapid-response technical teams.
              </p>
              <p>
                From site surveys to full-scale rack and stack operations, we provide the boots-on-the-ground expertise required to keep your projects moving forward without delay.
              </p>
            </div>

            {}
            <div className="bg-white/5 backdrop-blur-md p-6 md:p-8 rounded-lg border border-border shadow-sm">
              <h3 className="text-primary text-xs font-bold tracking-widest uppercase mb-5 font-mono">
                Service Advantages
              </h3>
              <ul className="space-y-3">
                {benefits.map((benefit, i) => <li key={i} data-index={i} className="flex items-start gap-3 text-sm md:text-base text-foreground/80 font-default">
                    <span className="w-1.5 h-1.5 rounded-lg bg-primary shrink-0 mt-2"></span>
                    <span>{benefit}</span>
                  </li>)}
              </ul>
            </div>

            {}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {specs.map((spec, i) => <div key={i} data-index={i} className="border border-border rounded-lg p-5 flex flex-col gap-2 bg-card hover:bg-accent/10 transition-colors duration-300">
                  <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider font-mono font-semibold">
                    <spec.icon className="w-4 h-4 text-primary" />
                    {spec.label}
                  </div>
                  <div className="text-foreground font-medium text-sm md:text-base font-default">
                    {spec.value}
                  </div>
                </div>)}
            </div>

            {}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-8 py-6 text-xs tracking-widest uppercase w-full sm:w-auto font-mono">
                <Link to="/ServicesPage">
                  Get Started
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-primary text-primary hover:bg-primary/10 rounded-lg px-8 py-6 text-xs tracking-widest uppercase w-full sm:w-auto font-mono">
                <Link to="/ContactPage">
                  Request Quote
                </Link>
              </Button>
            </div>

            {}
            <p className="text-sm text-muted-foreground italic mt-2 font-default">
              Need a custom solution for your business?{" "}
              <Link to="/ContactPage" className="text-primary hover:underline not-italic font-mono font-medium ml-1">
                Consult with an expert
              </Link>
            </p>

          </div>
        </div>
      </div>
    </section>;
}